import { Hash, ShieldCheck, Users } from "lucide-react";
import { Link } from "react-router-dom";

import { ChamaLogo } from "@/components/chama/ChamaLogo";
import { cn } from "@/lib/utils";
import type { ChannelState } from "@/lib/concord/editions";

interface ChannelSidebarProps {
  communityName: string;
  channels: ChannelState[];
  activeChannelId: string | undefined;
  memberCount: number;
  isModerator: boolean;
  onSelect: (id: string) => void;
}

export function ChannelSidebar({
  communityName,
  channels,
  activeChannelId,
  memberCount,
  isModerator,
  onSelect,
}: ChannelSidebarProps) {
  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex items-center gap-3 border-b border-sidebar-border px-4 py-4">
        <ChamaLogo className="size-9" />
        <div className="min-w-0">
          <h1 className="truncate font-display text-lg font-semibold leading-tight">
            {communityName}
          </h1>
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="size-3" />
            {memberCount} {memberCount === 1 ? "member" : "members"}
          </p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-2" aria-label="Channels">
        <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Channels
        </p>
        <ul className="space-y-0.5">
          {channels.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors",
                  c.id === activeChannelId
                    ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                    : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                )}
              >
                <Hash className="size-4 shrink-0" />
                <span className="truncate">{c.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {isModerator && (
        <div className="border-t border-sidebar-border p-2">
          <Link
            to="/moderate"
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
          >
            <ShieldCheck className="size-4" />
            Moderation
          </Link>
        </div>
      )}
    </div>
  );
}
